import cn from 'classnames';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ApproveCampaignBudgetModal } from '@features/campaigns/details/components/Modals/ApproveCampaignBudgetModal/ApproveCampaignBudgetModal';
import { ApproveCampaignModal } from '@features/campaigns/details/components/Modals/ApproveCampaignModal/ApproveCampaignModal';
import { useActionButtonsPermissions } from '@features/campaigns/details/hooks/useActionButtonsPermissions';
import { useApproveCampaign } from '@features/campaigns/details/hooks/useApproveCampaign';
import { useApproveCampaignBudget } from '@features/campaigns/details/hooks/useApproveCampaignBudget';
import { useModalState } from '@features/campaigns/details/hooks/useModalState';
import { createRedirectToEditFormHandler } from '@features/campaigns/details/utils/actionButtonHandlers';
import { DeleteCampaignModal } from '@features/campaigns/shared/components/Modals/DeleteCampaignModal/DeleteCampaignModal';
import { SubmitCampaignModal } from '@features/campaigns/shared/components/Modals/SubmitCampaignModal/SubmitCampaignModal';
import useDeleteCampaign from '@features/campaigns/shared/hooks/useDeleteCampaign.tsx';
import useSubmitCampaign from '@features/campaigns/shared/hooks/useSubmitCampaign';
import { AccessRight } from '@shared/api/commonApiInterfaces';
import { i18nNameSpace } from '@shared/consts/i18n';
import { routePaths } from '@shared/router/routePaths';

import styles from './ActionButtons.module.scss';

interface ActionButtonsProps {
  shouldSubmitButtonBeDisabled: boolean;
  shouldApproveButtonBeDisabled: boolean;
  campaignName: string;
  campaignAccessRights: AccessRight[];
}

function ActionButtons({
  shouldSubmitButtonBeDisabled,
  shouldApproveButtonBeDisabled,
  campaignName,
  campaignAccessRights,
}: ActionButtonsProps) {
  const { t } = useTranslation(i18nNameSpace.CAMPAIGNS);
  const navigate = useNavigate();
  const { campaignId } = useParams();

  const modalState = useModalState();
  const { canDeleteCampaign, canApproveCampaign, canSubmitCampaign, canApproveCampaignBudget, showEditButton } =
    useActionButtonsPermissions({ campaignAccessRights });

  const { deleteCampaignMutation, deleteCampaignIsPending } = useDeleteCampaign({
    handleToggleDeleteModalVisibility: modalState.handleToggleDeleteModalVisibility,
    redirectToUrl: routePaths.campaigns,
  });

  const { mutate: submitCampaignMutation, isPending: submitCampaignIsPending } = useSubmitCampaign({
    campaignId: campaignId!,
    campaignName,
    handleToggleModalVisibility: modalState.handleToggleSubmitModalVisibility,
  });

  const { approveCampaignIsPending, handleApproveConfirmation, isApproveCampaignRequestCompleted } = useApproveCampaign(
    {
      campaignId: campaignId!,
      campaignName,
      handleToggleModalVisibility: modalState.handleToggleApproveModalVisibility,
    },
  );

  const { approveCampaignBudgetIsPending, handleApproveBudgetConfirmation } = useApproveCampaignBudget({
    campaignId: campaignId!,
    campaignName,
    handleToggleModalVisibility: modalState.handleToggleApproveBudgetModalVisibility,
  });

  // Utility functions
  const redirectToEditForm = createRedirectToEditFormHandler(navigate, campaignId!);
  const continueSubmittingCampaignHandler = () => submitCampaignMutation();
  const handleDeleteConfirmation = () => {
    if (campaignId) {
      deleteCampaignMutation(campaignId);
    }
  };

  return (
    <>
      <div className={styles.actionButtonsWrapper}>
        {canDeleteCampaign && (
          <Button
            className={'p-button p-button-outlined'}
            data-testid='delete-campaign-button'
            onClick={modalState.handleToggleDeleteModalVisibility}
          >
            <i className={cn('pi pi-trash', styles.buttonIcon)}></i>
            <span>{t('page.campaignDetails.actions.delete')}</span>
          </Button>
        )}
        {showEditButton && (
          <Button
            className={'p-button p-button-outlined'}
            data-testid='edit-campaign-button'
            onClick={redirectToEditForm}
          >
            <i className={cn('pi pi-pencil', styles.buttonIcon)}></i>
            <span>{t('page.campaignDetails.actions.edit')}</span>
          </Button>
        )}
        {canSubmitCampaign && (
          <Button
            className={'p-button'}
            data-testid='submit-campaign-button'
            disabled={shouldSubmitButtonBeDisabled}
            loading={submitCampaignIsPending}
            onClick={modalState.handleToggleSubmitModalVisibility}
          >
            {t('page.campaignDetails.actions.submit')}
          </Button>
        )}
        {canApproveCampaign && (
          <Button
            disabled={shouldApproveButtonBeDisabled || approveCampaignIsPending}
            onClick={modalState.handleToggleApproveModalVisibility}
            className={'p-button'}
            data-testid='approve-campaign-button'
          >
            {t('page.campaignDetails.actions.approve')}
          </Button>
        )}
        {canApproveCampaignBudget && (
          <Button
            onClick={modalState.handleToggleApproveBudgetModalVisibility}
            className={'p-button'}
            data-testid='approve-budget-button'
          >
            {t('page.campaignDetails.actions.approveBudget')}
          </Button>
        )}
      </div>
      <SubmitCampaignModal
        isVisible={modalState.isSubmitModalVisible}
        toggleModalVisibility={modalState.handleToggleSubmitModalVisibility}
        submitCallback={continueSubmittingCampaignHandler}
        campaignName={campaignName}
        submitCallbackActionIsLoading={submitCampaignIsPending}
      />
      <ApproveCampaignModal
        isVisible={modalState.isApproveModalVisible}
        toggleModalVisibility={modalState.handleToggleApproveModalVisibility}
        approveCallback={handleApproveConfirmation}
        campaignName={campaignName}
        submitCallbackActionIsLoading={approveCampaignIsPending}
        submitCallbackActionIsCompleted={isApproveCampaignRequestCompleted}
      />
      <ApproveCampaignBudgetModal
        isVisible={modalState.isApproveBudgetModalVisible}
        toggleModalVisibility={modalState.handleToggleApproveBudgetModalVisibility}
        approveCallback={handleApproveBudgetConfirmation}
        campaignName={campaignName}
        submitCallbackActionIsLoading={approveCampaignBudgetIsPending}
      />
      <DeleteCampaignModal
        isVisible={modalState.isDeleteModalVisible}
        toggleModalVisibility={modalState.handleToggleDeleteModalVisibility}
        deleteCallback={handleDeleteConfirmation}
        campaignName={campaignName}
        deleteCallbackActionIsLoading={deleteCampaignIsPending}
      />
    </>
  );
}

export default ActionButtons;
